import React, {
  useState,
  useMemo,
  useEffect
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import omit from 'lodash/omit';

import { required } from '../helpers/formValidators';
import api from '../api';

import Modal from './Modal';
import ProfileDeleteModal from './ProfileDeleteModal';
import {
  FormIconChooser,
  FormFeedback,
  FormInput,
  FormLabel,
  FormRadio,
  FormSelect,
  FormCheckbox,
  BaseForm
} from './form';
import { PrimaryButton, SecondaryButton } from './buttons';
import profileIcons from './icons/profileIcons';
import './ProfileEditorModal.css';
import ErrorMessage from './ErrorMessage/ErrorMessage';
import { getProfiles } from '../selectors/profiles';
import { getAuthenticatedAccountId } from '../selectors/auth';

const ProfileEditorModal = props => {
  const {
    isEditingCurrentProfile,
    isErrorMessageVisible,
    isLoading,
    isOpen,
    isPrimary,
    isRoleOtherSelected,
    t,
    initialValues,
    onRequestClose,
    updateProfile,
    accountId,
    createProfile
  } = props;

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [uniqueName, setUniqueName] = useState(true);

  const iconsForChooser = useMemo(
    () =>
      Object.keys(profileIcons).map(icon => (
        <FormRadio
          key={icon}
          name="icon"
          value={icon}
          imageSrc={profileIcons[icon]}
          disabled={isLoading}
          initialValue={initialValues && initialValues.icon}
        />
      )),
    [isLoading]
  );

  const openDeleteModal = () => {
    if (!isLoading) {
      setIsDeleteModalOpen(true);
    }
  };

  const closeDeleteModal = () => {
    if (!isLoading) {
      setIsDeleteModalOpen(false);
    }
  };

  const isNew = !initialValues;
  const profileId = !isNew ? initialValues.id : null;

  // reset unique name each time the form opens
  useEffect(() => {
    setUniqueName(true);
  }, [isOpen]);

  const roles = useMemo(
    () =>
      props.roles.map(role => ({
        value: role.id,
        label: role.other
          ? `${t(`roles.${role.name}`)} ${t('profile.roleOtherFillInBelow')}`
          : t(`roles.${role.name}`)
      })),
    [props.roles, t]
  );

  const handleSubmit = (values) => {
    if (profileId) {
      updateProfile(
        { accountId, profileId },
        {
          body: JSON.stringify({
            ...omit(values, 'roleId'),
            roleId: values.roleId && values.roleId.value ? values.roleId.value : values.roleId,
            status: 'active'
          })
        }
      ).then((data) => {
        if (data) {
          onRequestClose();
        }
      }).catch((error) => {
        return <ErrorMessage />
      })
    } else {
      createProfile(
        { accountId },
        {
          body: JSON.stringify({
            ...values,
            status: 'active'
          })
        }
      ).then((data) => {
        if (data) {
          onRequestClose();
        }
      }).catch((error) => {
        return <ErrorMessage />;
      })
    }


  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      className="profile-editor-modal"
      showCloseButton={!isLoading}
      shouldCloseOnOverlayClick={false}
    >
      <div className="profile-editor__header">
        <h3>
          {isNew ? t('profile.addProfileTitle') : t('profile.editProfileTitle')}
        </h3>
      </div>

      <BaseForm
        className={classNames('profile-editor', {
          'profile-editor--new': isNew,
          'profile-editor--primary': isPrimary
        })}
        onSubmit={handleSubmit}
        name="profileEditor"
      >
        <div className="profile-editor__fields">
          <div className="profile-editor__name-fields">
            <FormInput
              disabled={isLoading}
              labelRenderer={() => (
                <FormLabel>{t('profile.firstNameLabel')}</FormLabel>
              )}
              id="profile.firstName"
              name="firstName"
              placeholder={t('profile.firstNamePlaceholder')}
              type="text"
              initialValue={initialValues && initialValues.firstName}
              validate={required}
            />
            <FormInput
              labelRenderer={() => (
                <FormLabel>{t('profile.lastNameLabel')}</FormLabel>
              )}
              id="profile.lastName"
              name="lastName"
              placeholder={t('profile.lastNamePlaceholder')}
              type="text"
              initialValue={initialValues && initialValues.lastName}
              validate={required}
            />
          </div>
          <FormLabel>{t('profile.roleLabel')}</FormLabel>
          <FormSelect
            disabled={isLoading}
            id="profile.roleId"
            name="roleId"
            placeholder={t('profile.rolePlaceholder')}
            initialValue={initialValues && {value: initialValues.roleId, label: roles.find(role => role.value === initialValues.roleId).label}}
            options={roles}
            validate={required}
          />
          {isRoleOtherSelected && (
            <FormInput
              disabled={isLoading}
              id="profile.roleOther"
              name="roleOther"
              placeholder={t('profile.roleOtherPlaceholder')}
              type="text"
            />
          )}
        </div>
        <div className="profile-editor__icon-field">
          <FormLabel>{t('profile.iconLabel')}</FormLabel>
          <FormIconChooser>{iconsForChooser}</FormIconChooser>
        </div>
        {isErrorMessageVisible && (
          <FormFeedback positive={false}>
            {t('profile.formErrorMessage')}
          </FormFeedback>
        )}
        {!uniqueName && (
          <React.Fragment>
            <FormFeedback positive={false}>
              There is already a profile with that name
            </FormFeedback>
            <FormCheckbox name="forceCreate" value={false} required>
              Create Duplicate Profile Name
            </FormCheckbox>
          </React.Fragment>
        )}
        <div className="profile-editor__footer">
          {!isNew && !isPrimary && (
            <SecondaryButton
              className="profile-editor__delete"
              colorScheme="red"
              disabled={isLoading}
              type="button"
              onClick={openDeleteModal}
            >
              {t('profile.delete')}
            </SecondaryButton>
          )}
          <PrimaryButton
            className="profile-editor__save"
            colorScheme="blue"
            // disabled={isLoading || isButtonDisabled}
            type="submit"
            loading={isLoading}
          >
            {t('profile.save')}
          </PrimaryButton>
        </div>
      </BaseForm>
      {!isNew && (
        <ProfileDeleteModal
          isOpen={isDeleteModalOpen}
          isLoading={isLoading}
          isCurrentProfile={isEditingCurrentProfile}
          onCancel={closeDeleteModal}
          onConfirm={props.onDelete}
          profileId={profileId}
        />
      )}
    </Modal>
  );
};

ProfileEditorModal.defaultProps = {
  initialValues: null
};

ProfileEditorModal.propTypes = {
  initialValues: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  isButtonDisabled: PropTypes.bool.isRequired,
  isEditingCurrentProfile: PropTypes.bool.isRequired,
  isErrorMessageVisible: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isOpen: PropTypes.bool.isRequired,
  isPrimary: PropTypes.bool.isRequired,
  isRoleOtherSelected: PropTypes.bool.isRequired,
  onRequestClose: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  roles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      other: PropTypes.bool
    })
  ).isRequired,
  t: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
  profiles: getProfiles(state),
  accountId: getAuthenticatedAccountId(state)
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      updateProfile: api.actions.profilesPatch,
      createProfile: api.actions.profilesPost
    },
    dispatch
);

export default connect(mapStateToProps, mapDispatchToProps)(ProfileEditorModal);




// WEBPACK FOOTER //
// ./src/components/ProfileEditorModal.js